import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import LoadableImage from "common/components/ui/LoadableImage";
import Skeleton from "common/components/ui/Skeleton";
import { getMonthName } from "common/helpers/utils";
import type { WorkExperience } from "common/types";

import ExpandableText from "../../../../pages/leads/components/LeadDetails/ExpandableText";

import CompanyFallbackImage from "assets/images/profile-placeholders/company.png";

function formatRange(from: string, to: string) {
  return [from, to].filter((value) => !!value).join(" - ");
}
function formatMonthAndYear(data: { month: number; year: number }) {
  const { month, year } = data;
  const monthName = getMonthName(month - 1);
  if (monthName) {
    return `${monthName} ${year}`;
  }
  return year.toString();
}
export default function CurrentExperience({
  currentExperience,
  isLoading,
}: {
  currentExperience: WorkExperience[];
  isLoading: boolean;
}) {
  const [selectedExperienceIndex, setSelectedExperienceIndex] = useState(0);

  if (currentExperience.length || isLoading) {
    let content = (
      <>
        <div className="flex items-center gap-x-1.5">
          <Skeleton className="rounded-2xl size-8" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex flex-col mt-2 gap-y-1">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-36" />
        </div>
      </>
    );
    if (currentExperience.length) {
      const {
        company,
        end_date: endDate,
        start_date: startDate,
        description,
        location,
        job_title: jobTitle,
      } = currentExperience[selectedExperienceIndex];
      content = (
        <>
          <div className="flex items-start gap-x-1.5">
            <LoadableImage
              src={company?.logo_url}
              className="rounded-2xl size-8"
              fallbackImage={CompanyFallbackImage}
            />
            <div className="mr-0.5 flex flex-1 flex-col overflow-hidden">
              <span className="text-xs-bold">{jobTitle}</span>
              {company && (
                <span className="text-button-xxs text-black-500 truncate">
                  {company.name}
                </span>
              )}
            </div>
            <div className="flex flex-col items-end">
              {location && (
                <span className="text-button-xxs text-black-500">
                  {location.name}
                </span>
              )}
              <span className="text-button-xxs text-black-500">
                {formatRange(
                  formatMonthAndYear(startDate),
                  endDate ? formatMonthAndYear(endDate) : "Present",
                )}
              </span>
            </div>
          </div>
          {description && <ExpandableText>{description}</ExpandableText>}
        </>
      );
    }
    return (
      <div className="flex flex-col border-y border-y-black-200 px-4 pb-2">
        <div className="flex items-center justify-between py-2.5">
          <span className="text-button-sm">
            Current position{currentExperience.length > 1 && "s"}
          </span>
          {currentExperience.length > 1 && (
            <div className="flex items-center gap-x-2">
              <Button
                disabled={selectedExperienceIndex === 0}
                variant="default-black"
                size="xs"
                intent="iconOnly"
                onClick={() =>
                  setSelectedExperienceIndex(selectedExperienceIndex - 1)
                }
              >
                <CaretLeft />
              </Button>
              <span className="text-button-sm">
                {selectedExperienceIndex + 1}/{currentExperience.length}
              </span>
              <Button
                disabled={
                  selectedExperienceIndex === currentExperience.length - 1
                }
                variant="default-black"
                size="xs"
                intent="iconOnly"
                onClick={() =>
                  setSelectedExperienceIndex(selectedExperienceIndex + 1)
                }
              >
                <CaretRight />
              </Button>
            </div>
          )}
        </div>
        {content}
      </div>
    );
  }
  return (
    <div className="mx-4 flex flex-col items-center rounded-4xl  border border-purple-200 bg-black-50 p-12">
      <div className="mb-4 flex w-44 items-center gap-2 rounded-3xl bg-white p-2">
        <div className="flex size-8 items-center justify-center rounded-lg bg-black-300 text-white">
          <X size={24} />
        </div>
        <div className="flex flex-col gap-y-1">
          <div className="h-1.5 w-16 rounded-full bg-black-300" />
          <div className="h-1 w-28 rounded-full bg-black-100" />
          <div className="h-1 w-14 rounded-full bg-black-100" />
        </div>
      </div>
      <p className="text-center text-sm-bold">
        This lead does not have any current positions
      </p>
    </div>
  );
}
